import React from 'react';

/**
 * What a SAP update actually did to our data.
 *
 * "Received and applied" is a claim; this is the evidence behind it. Someone with
 * the SAP screen open can compare our numbers to theirs line by line, which is the
 * only way to answer "did it save correctly" rather than just "did it arrive".
 *
 * Shared by the admin monitor and the user-facing check page: the two screens answer
 * different questions, but neither is worth much if it cannot show this.
 */

function num(n) {
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : String(Math.round(v * 1000) / 1000);
}

/** Signed movement, so a reader is not left subtracting two numbers themselves. */
function Delta({ from, to }) {
  const d = Number(to) - Number(from);
  if (d === 0) return <span className="text-gray-400">no change</span>;
  const up = d > 0;
  return (
    <span className={up ? 'text-green-700' : 'text-red-700'}>
      {up ? 'in' : 'out'} {up ? '+' : '−'}{num(Math.abs(d))}
    </span>
  );
}

/**
 * What our table holds now, against the figure this event carried.
 *
 * These disagree routinely, and it does not mean anything is wrong. The event
 * carries the quantity as it stood when SAP raised it; our row carries the
 * quantity SAP reports now, because processing an event re-reads the item rather
 * than subtracting the movement. SAP posts several documents a minute against the
 * same items, so by the time an event is handled the live figure has usually moved
 * on again.
 *
 * This used to be shown in red as "≠ saved", which read as a fault. It sent people
 * hunting for a stock discrepancy that did not exist — every item checked against
 * SAP agreed exactly. A difference here is history, not drift, and it is shown as
 * such; the figure on the left is the one to trust, because it came from SAP.
 */
function DbNow({ saved, now }) {
  if (!now) return <span className="text-gray-400">—</span>;
  const agrees = Number(now.in_stock) === Number(saved);
  if (agrees) return <span className="text-green-700">✓ {num(now.in_stock)}</span>;
  return (
    <span
      className="text-gray-600"
      title={'This event was raised when the quantity was ' + num(saved)
        + '. Our row holds ' + num(now.in_stock) + ', read from SAP when the event was '
        + 'processed — later movements are already included. Not a discrepancy.'}
    >
      {num(now.in_stock)}
      <span className="text-gray-400 ml-1">· newer</span>
    </span>
  );
}

export function parseResult(row) {
  let result = row.result_json;
  if (typeof result === 'string') {
    try { result = JSON.parse(result); } catch { result = null; }
  }
  return result || null;
}

export default function WhatChanged({ row }) {
  const result = parseResult(row);
  // Events processed before this was recorded have nothing to show, which is not the
  // same as having changed nothing.
  if (!result) return <span className="text-gray-400">—</span>;

  const changes = Array.isArray(result.changes) ? result.changes : [];

  if (changes.length) {
    return (
      <div>
        {(result.fromWarehouse || result.toWarehouse) && (
          <div className="text-xs text-gray-600 mb-1">
            Transferred from <b>WH{result.fromWarehouse}</b> to <b>WH{result.toWarehouse}</b>
          </div>
        )}
        <table className="text-xs border-collapse">
          <thead>
            <tr className="text-gray-400">
              <th className="text-left pr-3 font-medium">Item</th>
              <th className="text-left pr-3 font-medium">Wh</th>
              <th className="text-right pr-3 font-medium">Was</th>
              <th className="text-right pr-3 font-medium">Now</th>
              <th className="text-left pr-3 font-medium">Movement</th>
              <th className="text-right pr-3 font-medium">In our DB now</th>
              <th className="text-right font-medium">Committed</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((c, i) => (
              <tr key={i} className="align-top">
                <td className="pr-3 font-mono text-gray-900 whitespace-nowrap">{c.itemCode}</td>
                <td className="pr-3 text-gray-500 whitespace-nowrap">WH{c.warehouseCode}</td>
                <td className="pr-3 text-right text-gray-500">{c.from ? num(c.from.in_stock) : '—'}</td>
                <td className="pr-3 text-right font-medium text-gray-900">{num(c.to.in_stock)}</td>
                <td className="pr-3 whitespace-nowrap">
                  {c.from ? <Delta from={c.from.in_stock} to={c.to.in_stock} />
                    : <span className="text-gray-500">row added</span>}
                </td>
                {/* Read back from item_warehouses when the page loaded, not from the
                    event. The event says what we wrote then; this says what is in the
                    table now, which is what someone is really asking. */}
                <td className="pr-3 text-right whitespace-nowrap">
                  <DbNow saved={c.to.in_stock} now={c.dbNow} />
                </td>
                <td className="text-right text-gray-500">
                  {c.from && c.from.committed !== c.to.committed
                    ? `${num(c.from.committed)} → ${num(c.to.committed)}`
                    : num(c.to.committed)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {result.moved > changes.length && (
          <div className="text-xs text-gray-400 mt-1">
            …and {result.moved - changes.length} more rows
          </div>
        )}
      </div>
    );
  }

  // Non-stock objects describe themselves in their own terms.
  if (result.status) return <span className="text-xs">Order status now <b>{result.status}</b></span>;
  if (result.planning) {
    return (
      <span className="text-xs">
        Planning <b>{result.planning}</b>, procurement <b>{result.procurement}</b>
      </span>
    );
  }
  if (result.treeCode) {
    return (
      <span className="text-xs">
        Bill of materials {result.action === 'created' ? 'created' : 'refreshed'}
      </span>
    );
  }
  if (result.action === 'stock-refreshed') {
    return <span className="text-xs text-gray-500">Checked — our stock already matched SAP</span>;
  }
  if (result.reason) return <span className="text-xs text-gray-500">{result.reason}</span>;
  return <span className="text-gray-400">—</span>;
}
